// One-time profile location backfill for pre-v8 users.
// Reads profiles that have a `city` string but no coordinates, resolves
// city → {lat, lng, state, country} via Mapbox forward geocoding, and
// patches the row. Idempotent — re-runs only touch rows still missing data.
import { supabase } from "@/lib/supabase";
import { searchCities } from "@/lib/citySearch";

/**
 * Fetch all profiles needing a backfill in one paged query.
 * Selects: id, city, state, country, location_lat, location_lng.
 * We rely on the admin RLS policy from v7 (admins can read/update any profile).
 */
export async function findProfilesNeedingBackfill() {
  // OR clause: location_lat IS NULL OR location_lng IS NULL.
  // We also require `city IS NOT NULL` and `is_deleted = false` to skip
  // soft-deleted accounts.
  const { data, error } = await supabase
    .from("profiles")
    .select("id, username, display_name, city, state, country, location_lat, location_lng, is_deleted")
    .not("city", "is", null)
    .or("location_lat.is.null,location_lng.is.null");
  if (error) throw error;
  return (data || []).filter((p) => !p.is_deleted);
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Resolve one city string. Retries twice on transient errors.
 * Returns { city, state, country, lat, lng } or null when unresolved.
 */
async function resolveCity(city) {
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const results = await searchCities(city, { limit: 1 });
      const r = results[0];
      if (!r || r.lat == null || r.lng == null) return null;
      return r;
    } catch {
      // Network blip — back off and retry.
      await sleep(400 * (attempt + 1));
    }
  }
  return null;
}

/**
 * Run the backfill.
 *
 *   rows      — array from findProfilesNeedingBackfill()
 *   dryRun    — when true, geocode + log but do not write to Supabase
 *   onProgress({ done, total, last }) — UI callback per row
 *   onLog(line) — UI callback for the streaming log
 *   throttleMs — delay between Mapbox calls (default 250ms ≈ 4 rps)
 *   batchSize  — write rows in chunks (default 8). Each chunk is awaited
 *                before the next so we don't blow up Supabase's rate limit.
 */
export async function runBackfill({
  rows,
  dryRun = true,
  onProgress = () => {},
  onLog = () => {},
  throttleMs = 250,
  batchSize = 8,
}) {
  const total = rows.length;
  let done = 0;
  const summary = { total, skipped: 0, resolved: 0, written: 0, failed: 0 };

  // Process serially but with a tiny throttle so Mapbox is happy.
  // We accumulate writes into a batch and flush every `batchSize`.
  let pending = []; // { id, patch }

  const flush = async () => {
    if (!pending.length) return;
    if (dryRun) {
      onLog(`[dry-run] would write ${pending.length} rows`);
      pending = [];
      return;
    }
    // We update one-by-one because `upsert` would require all fields; each
    // patch is a tiny PATCH /profiles?id=eq.<id> request, which Supabase
    // batches at the network level efficiently.
    const chunk = pending;
    pending = [];
    const results = await Promise.allSettled(
      chunk.map((p) =>
        supabase.from("profiles").update(p.patch).eq("id", p.id)
      )
    );
    results.forEach((r, i) => {
      if (r.status === "fulfilled" && !r.value.error) {
        summary.written += 1;
        onLog(`✓ wrote ${chunk[i].id.slice(0, 8)} (${chunk[i].patch.city})`);
      } else {
        summary.failed += 1;
        const msg = r.status === "rejected" ? r.reason?.message : r.value?.error?.message;
        onLog(`✗ failed ${chunk[i].id.slice(0, 8)} :: ${msg || "unknown"}`);
      }
    });
  };

  for (const row of rows) {
    // Defensive: if BOTH coords are already present somehow, skip without
    // burning a Mapbox call. (findProfilesNeedingBackfill should not return
    // these, but tolerate.)
    if (row.location_lat != null && row.location_lng != null) {
      summary.skipped += 1;
      done += 1;
      onProgress({ done, total, last: { id: row.id, status: "skipped" } });
      continue;
    }

    const resolved = await resolveCity(row.city);
    await sleep(throttleMs);

    if (!resolved) {
      summary.failed += 1;
      done += 1;
      onLog(`? unresolved "${row.city}" (${row.username || row.id.slice(0, 8)})`);
      onProgress({ done, total, last: { id: row.id, status: "unresolved" } });
      continue;
    }

    summary.resolved += 1;

    // Build a patch that NEVER overwrites valid existing coordinates.
    // We may still enrich state/country if those are missing.
    const patch = {};
    if (row.location_lat == null) patch.location_lat = resolved.lat;
    if (row.location_lng == null) patch.location_lng = resolved.lng;
    // city stays as the user typed it; we only sync canonical state/country.
    if (!row.state && resolved.state) patch.state = resolved.state;
    if (!row.country && resolved.country) patch.country = resolved.country;
    patch.city = row.city; // keep — used to scope the write below as well

    if (Object.keys(patch).length === 1) {
      // Only `city` in patch means nothing new — skip.
      summary.skipped += 1;
      done += 1;
      onProgress({ done, total, last: { id: row.id, status: "nochange" } });
      continue;
    }

    onLog(`→ ${row.username || row.id.slice(0, 8)} : "${row.city}" → ${resolved.lat.toFixed(3)}, ${resolved.lng.toFixed(3)}${resolved.state ? " · " + resolved.state : ""}${resolved.country ? " · " + resolved.country : ""}`);

    pending.push({ id: row.id, patch });
    if (pending.length >= batchSize) {
      await flush();
    }

    done += 1;
    onProgress({ done, total, last: { id: row.id, status: "queued" } });
  }

  await flush();
  return summary;
}
